import { useState, useRef } from 'react';
import clsx from 'clsx';
import styles from './TagInput.module.css';

/**
 * Tag input component for entering multiple keywords/tags.
 *
 * @param {Object} props
 * @param {string[]} props.value - Array of tag strings
 * @param {(tags: string[]) => void} props.onChange - Callback when tags change
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.label] - Label text
 * @param {boolean} [props.disabled] - Disable the input
 * @param {boolean} [props.inherited] - The tags are INHERITED, not stored here.
 *   Renders them dimmed instead of as a comma-joined placeholder string, so a
 *   list field looks the same in every scope -- one way of showing keywords,
 *   not two. Editing still calls onChange with the full resulting list, which
 *   is what adopts the inherited set as this scope's own.
 * @param {string} [props.className] - Additional CSS classes
 *
 * @example
 * <TagInput
 *   label="Keywords"
 *   value={keywords}
 *   onChange={setKeywords}
 *   placeholder="Add keyword..."
 * />
 */
export default function TagInput({
  value = [],
  onChange,
  placeholder = 'Add tag...',
  label,
  disabled = false,
  inherited = false,
  className,
}) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  const handleKeyDown = (e) => {
    if (disabled) return;

    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      // Remove last tag if input is empty and backspace pressed
      removeTag(value.length - 1);
    }
  };

  const handlePaste = (e) => {
    if (disabled) return;

    const pastedText = e.clipboardData.getData('text');
    if (!pastedText.includes(',')) {
      // No comma, let default paste behavior handle it
      return;
    }

    e.preventDefault();

    // Split by comma, trim whitespace, filter empty and duplicates
    const newTags = pastedText
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag && !value.includes(tag));

    if (newTags.length > 0) {
      onChange([...value, ...newTags]);
    }
    setInputValue('');
  };

  const addTag = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInputValue('');
  };

  const removeTag = (index) => {
    if (disabled) return;
    const newTags = [...value];
    newTags.splice(index, 1);
    onChange(newTags);
  };

  const handleContainerClick = () => {
    if (!disabled) {
      inputRef.current?.focus();
    }
  };

  return (
    <div className={clsx(styles.tagInput, className)}>
      {label && <label className={styles.label}>{label}</label>}
      <div
        className={clsx(styles.container, disabled && styles.disabled)}
        onClick={handleContainerClick}
      >
        <div className={styles.tags}>
          {value.map((tag, index) => (
            <span
              key={`${tag}-${index}`}
              className={clsx(styles.tag, inherited && styles.tagInherited)}
              title={inherited ? 'Inherited — editing adopts this list here' : undefined}
            >
              <span className={styles.tagText}>{tag}</span>
              {!disabled && (
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTag(index);
                  }}
                  aria-label={`Remove ${tag}`}
                >
                  <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                    <path d="M4.28 3.22a.75.75 0 00-1.06 1.06L6.94 8l-3.72 3.72a.75.75 0 101.06 1.06L8 9.06l3.72 3.72a.75.75 0 101.06-1.06L9.06 8l3.72-3.72a.75.75 0 00-1.06-1.06L8 6.94 4.28 3.22z" />
                  </svg>
                </button>
              )}
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onBlur={addTag}
            placeholder={value.length === 0 ? placeholder : ''}
            disabled={disabled}
          />
        </div>
      </div>
      <span className={styles.hint}>Press Enter or comma to add. Paste comma-separated values to add multiple.</span>
    </div>
  );
}
